import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {
  pullRequestsChangesRoute,
  pullRequestsChangesWithRangeRoute,
  pullRequestsFilesRoute,
  pullRequestsFilesWithRangeRoute,
} from './files-changed-routes'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Suspense, useEffect, useEffectEvent} from 'react'
import type {PullRequest} from '../types'
import type {FilesRoutePayload} from './route-payload-types'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'
import {useMemoryStats} from '@github-ui/use-memory-stats'
import {getCurrentDiffImplementation} from '../helpers/get-diff-implementation'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'

interface FilesAnalyticsProps {
  viewSettings: FilesRoutePayload['user']['viewSettings']
}

export function ChangesEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Changes />
    </ErrorBoundary>
  )
}

export function ChangesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <ChangesWithRange />
    </ErrorBoundary>
  )
}

function Changes() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesRoute, 'mainQuery')

  return (
    <FilesChangedComponent
      pullRequest={pullRequest}
      routeData={routeData}
      // force remount when routeData updates after soft navigation so the component gets fresh data
      key={`${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`}
    />
  )
}

function ChangesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsChangesWithRangeRoute)

  return <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
}

export function FilesEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="FilesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Files />
    </ErrorBoundary>
  )
}

export function FilesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="FilesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <FilesWithRange />
    </ErrorBoundary>
  )
}

function Files() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesRoute, 'mainQuery')

  return (
    <Suspense>
      <FilesChangedComponent
        pullRequest={pullRequest}
        routeData={routeData}
        // force remount when routeData updates after soft navigation so the component gets fresh data
        key={`${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`}
      />
    </Suspense>
  )
}

function FilesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsFilesWithRangeRoute)

  return (
    <Suspense>
      <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
    </Suspense>
  )
}

export function FilesAnalytics({viewSettings}: FilesAnalyticsProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const sendPageView = useEffectEvent(() => {
    sendPullRequestAnalyticsEvent('page_view.files_page', 'PAGE_VIEW', {
      browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
      diff_view_setting: viewSettings.splitPreference,
      comments_setting: viewSettings.commentsPreference,
      line_spacing_setting: viewSettings.lineSpacing,
      change_groups_setting: viewSettings.showChangeGroups ? 'show' : 'hide',
    })
  })

  useEffect(() => {
    sendPageView()
  }, [])

  return null
}

export function FilesChangedComponent({
  pullRequest,
  routeData,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
}) {
  const diffImplementation = getCurrentDiffImplementation(routeData.virtualizeDiffEntries)
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isExtendedLimitsEnabled = useFeatureFlag('pull_request_virtualization_extended_limit')
  useMemoryStats({
    statName: 'PULL_REQUESTS_FILES_JS_HEAP',
    tags: {
      version: diffImplementation.version,
      virtualized: String(diffImplementation.virtualized),
    },
  })

  return (
    <AnalyticsProvider
      appName="pull-request-files"
      category="files_tab"
      metadata={{
        pull_request_id: pullRequest.id.toString(),
        base_sha: routeData.pullRequest.comparison.baseOid,
        head_sha: routeData.pullRequest.comparison.headOid,
        diff_version: diffImplementation.version,
        diff_virtualized: String(diffImplementation.virtualized),
        diff_line_limit_extended: String(isExtendedLimitsEnabled),
      }}
    >
      <FilesAnalytics viewSettings={routeData.user.viewSettings} />
      <Suspense>
        <DiffComparisonViewer.Viewer
          basePath={pullRequest.pathName}
          initialData={routeData}
          useAdvancedToolbar
          useChangeGroups
          useInlineComments
          useEditOptions
          useFileTree
        />
      </Suspense>
    </AnalyticsProvider>
  )
}
